import type { UpdateResolver } from '@urql/exchange-graphcache'
import { GET_CHATROOM_MESSAGES } from '@/components/MeetingRoom/MeetingRoomSidepanel/MeetingChat/services'
import { chatConfig } from '@/config/chat'
import { graphql } from '@/generated/gql'
import type {
    ChatMessageDeletedSubscription,
    ChatMessageSentSubscription,
    ChatMessageUpdatedSubscription,
    CreateChatMessageInput,
    CreateChatMessageV1Mutation,
    MutationDeleteChatMessageV1Args,
    SetLastSeenMessageMutation,
    SetLastSeenMessageMutationVariables,
} from '@/generated/gql/graphql'

export const chatMutationsUpdaters: Record<string, UpdateResolver> = {
    createChatMessageV1: (
        result: CreateChatMessageV1Mutation,
        args: { input: CreateChatMessageInput },
        cache,
    ) => {
        const chatMessage = result.createChatMessageV1.chatMessage
        const { meetingId, roomId } = args.input
        const variables = {
            input: { meetingId: meetingId, chatRoomId: roomId },
            last: chatConfig.pageSize,
        }
        cache.updateQuery(
            {
                query: GET_CHATROOM_MESSAGES,
                variables,
            },
            (data) => {
                if (data != null) {
                    data.meetingChatMessages.nodes.push(chatMessage)
                    return data
                }
                return null
            },
        )
    },
    deleteChatMessageV1: (
        _result,
        args: MutationDeleteChatMessageV1Args,
        cache,
    ) => {
        cache.invalidate({
            __typename: 'ChatMessage',
            id: args.input.messageId,
        })
    },
    setLastSeenMessageV1: (
        result: SetLastSeenMessageMutation,
        args: SetLastSeenMessageMutationVariables,
        cache,
    ) => {
        cache.invalidate('Query', 'meetingChatRooms', {
            input: { meetingId: args.input.meetingId },
        })
    },
}

export const chatSubscriptionsUpdaters: Record<string, UpdateResolver> = {
    chatMessageSentV1: (result: ChatMessageSentSubscription, _args, cache) => {
        const {
            meetingId,
            room: { id: chatRoomId },
        } = result.chatMessageSentV1

        cache.invalidate('Query', 'meetingChatMessages', {
            input: { meetingId: meetingId, chatRoomId: chatRoomId },
            last: chatConfig.pageSize,
        })
        cache.invalidate('Query', 'meetingChatRooms', {
            input: { meetingId: meetingId },
        })
    },
    chatMessageDeletedV1: (
        result: ChatMessageDeletedSubscription,
        _args,
        cache,
    ) => {
        const {
            message: { id },
            meetingId,
        } = result.chatMessageDeletedV1

        cache.invalidate({
            __typename: 'ChatMessage',
            id: id,
        })
        cache.invalidate('Query', 'meetingChatRooms', {
            input: { meetingId: meetingId },
        })
    },
    chatMessageUpdatedV1: (
        result: ChatMessageUpdatedSubscription,
        _args,
        cache,
    ) => {
        const {
            message: { body, id: messageId },
        } = result.chatMessageUpdatedV1

        cache.writeFragment(
            graphql(`
                  fragment UpdateChatMessage on ChatMessage {
                    id
                    body
                    isUpdated
                  }
                `),
            { id: messageId, body, isUpdated: true },
        )
    },
}
